import { makeStyles } from "@material-ui/core";
import { Linked } from "@type-r/models";
import { InblockIcon } from "common/invoice-aging/iconography";
import { Flex, asCurrency, asTimelessDateUTC, div, span } from "lqx-react";
import React from "react";
import { Invoice } from "../api/invoice";
import { InvoiceVaultCard } from "../api/invoiceVaultCard";
import { DigiCheckbox, DigiTooltip, limitText } from "../shared";
import { CheckboxColumn } from "../components/asset-section-styles";
import { useLinked } from "@type-r/react";

export const InvoiceVaultCardRenderer = (
    $active: Linked<InvoiceVaultCard>,
    $doUnarchive?,
    filterArchiveState?,
    actionButtonsProvider?,
    onClick?) =>
    ({ data, style, index }) => {
        const classes = useStyles();
        const vaultCard: InvoiceVaultCard = data.items[index];
        const invoice: Invoice = vaultCard.dataset;
        const isActive = $active.value == vaultCard;
        const InblockedIconClasses = useInblockedIconStyles();

        const isUnarchiving = () =>
            Boolean($doUnarchive.value.assetUids.length) &&
            $doUnarchive.value.needApproval == false &&
            $doUnarchive.value.assetUids.includes(vaultCard.uid);

        const onRowClicked = () => {
            $active.set(vaultCard);
            if (onClick) onClick(vaultCard);
        };

        const $vaultCardChecked = useLinked(vaultCard.checked).onChange(x => {
            vaultCard.checked = x;
        });
        
        return (
            <Row style={style}>
                <Flex row>
                    {
                        filterArchiveState &&
                        <CheckBoxContainer>
                            <CheckboxColumn>
                                <DigiCheckbox key={"checkbox-" + vaultCard.id} $checked={$vaultCardChecked} />
                            </CheckboxColumn>
                        </CheckBoxContainer>
                    }

                    <Container {...{ active: isActive.toString(), onClick: onRowClicked }}>
                        <Body {...{ active: isActive.toString() }} key={invoice.assetId}>
                            <Flex className={classes.infoCard}>
                                <Flex alignCenter>
                                    {invoice.isDigitized ? <InblockIcon src="/assets/images/inblock_logo.png" classes={InblockedIconClasses} /> : <BulletContainer><Bullet /></BulletContainer>} <State>{invoice.state.name}  </State>
                                    <DigiTooltip title={`Invoice ${invoice.number}`}>
                                        <InvoiceNumber>{limitText(invoice.number, 23)}</InvoiceNumber>
                                    </DigiTooltip>
                                    {vaultCard.isArchived && !isUnarchiving() ? <ModifyLabel> Archived</ModifyLabel> : ""}
                                    {isUnarchiving() ? <ModifyLabel> Unarchive is in progress</ModifyLabel> : ""}
                                </Flex>
                                <Flex>
                                    {actionButtonsProvider ? actionButtonsProvider(vaultCard, isUnarchiving()) : <ButtonsPlaceholder />}
                                </Flex>
                                <Flex className={classes.break}></Flex>
                                <Flex className={classes.companyName}>
                                    <SupplierAndBuyer title={invoice.supplier.name + ' - ' + invoice.buyer.name}>
                                        {invoice.supplier.name} - {invoice.buyer.name}
                                    </SupplierAndBuyer>
                                </Flex>
                            </Flex>
                        </Body>
                        <Footer {...{ active: isActive.toString() }} >
                            <FooterProperty><FooterLabel>Date:</FooterLabel> {asTimelessDateUTC(invoice.invoiceDate)}</FooterProperty>
                            <FooterProperty><FooterLabel>Due Date:</FooterLabel> {asTimelessDateUTC(invoice.invoiceDueDate)} </FooterProperty>
                            {!$active.value && <FooterProperty><FooterLabel>Face Value:</FooterLabel> {asCurrency(invoice.faceValue)} </FooterProperty>}
                            {!$active.value && <FooterProperty><FooterLabel>Promise To Pay Date:</FooterLabel> {asTimelessDateUTC(invoice.promisePayDate)}</FooterProperty>}

                        </Footer>
                    </Container>
                </Flex>
            </Row>
        )
    }

const useInblockedIconStyles = makeStyles({
    root: {
        display: 'flex',
        width: '15px',
        marginRight: '2px'
    }
});

const useStyles = makeStyles({
    infoCard: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
    },
    break: {
        flexBasis: '100%',
        height: 0
    },
    companyName: {
        marginTop: '-5px',
        maxWidth: '85%'
    }
});

const Row = div({
    width: '100%',
});

const CheckBoxContainer = div({
    width: '4%',
    maxWidth: '4%',
    height: '90px',
    paddingTop: '17px'
});

const Container = div({
    marginLeft: '5px',
    borderRadius: '5px',
    width: '95%',
    maxWidth: '95%',
    height: '90px',
    border: (props: any) => props.active === 'true' ? '1px solid #58C1EE' : '1px solid #F2F5F8'
});

const Body = div({
    borderTopLeftRadius: '5px',
    borderTopRightRadius: '5px',
    padding: '5px 10px 5px 10px',
    width: '100%',
    height: '60px',
    background: '#FFFFFF',
    backgroundColor: (props: any) => props.active === 'true' ? '#F4FAFD' : '#FFFFFF',
});

const Footer = div({
    gridArea: 'footer',
    borderTop: '2px solid #F2F5F8',
    borderLeft: '1px solid #F2F5F8',
    borderRight: '1px solid #F2F5F8',
    fontFamily: 'Montserrat',
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: '12px',
    height: '28px',
    overflow: 'hidden',
    borderBottomLeftRadius: '5px',
    borderBottomRightRadius: '5px',
    backgroundColor: (props: any) => props.active === 'true' ? '#E7F2F9' : '#F9FAFC',
    color: '#3E4B5F',
    display: 'flex',
    padding: '5px 10px',
    alignItems: 'center'
});

const FooterLabel = span({
    fontFamily: 'Montserrat',
    fontStyle: 'normal',
    fontWeight: 'normal',
    fontSize: '12px',
    lineHeight: '15px',
    color: '#829AB0',
});

const State = span({
    color: '#63a9dc',
    fontFamily: 'Montserrat',
    fontSize: '11px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '13px',
    letterSpacing: '0px',
    textAlign: 'left',
    paddingLeft: '3px'
});

const InvoiceNumber = span({
    color: '#bbc3cc',
    fontFamily: 'Montserrat',
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: '12px',
    marginLeft: '5px'
});

const SupplierAndBuyer = span({
    color: 'black',
    fontFamily: 'Montserrat',
    fontSize: '13px',
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: '16px',
    letterSpacing: '0px',
    textAlign: 'left',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    display: 'inline-block',
    maxWidth: '100%'
});

const Bullet = div({
    width: '6px',
    height: '6px',
    background: '#4297D6',
    borderRadius: '11px'
});

const BulletContainer = div({
    paddingTop: '15px',
    paddingBottom: '15px'
});

const ButtonsPlaceholder = div({
    minHeight: '35px'
});

const FooterProperty = span({
    flex: '0 0 250px'
});

export const ModifyLabel = span({
    marginLeft: '5px',
    color: '#f27324',
    fontSize: '12px',
    fontStyle: 'normal',
    lineHeight: '16px'
});